"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { ArrowLeft, Lightbulb, LogIn, Wand2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useSignInHref } from "@/lib/use-sign-in-href";
import { Container } from "@/components/ui/container";
import { Skeleton } from "@/components/ui/skeleton";
import { smoothScrollBehavior } from "@/lib/scroll-behavior";
import { Button } from "@/components/ui/button";
import { FixErrorModal } from "@/features/playground/components/FixErrorModal";
import { fixPracticeError } from "../api";
import { useAuth } from "@/components/providers/auth-provider";
import {
  useJudgeProblem,
  useJudgeRuntimes,
  useMySubmissions,
  usePracticeSubmission,
  useRunPracticeSample,
  useSubmitPractice,
} from "../hooks";
import { judgeErrorMessageKey } from "../lib/format";
import { preferredJudgeRuntime, rememberRuntime } from "../lib/last-runtime";
import { ArchiveCredit } from "./ArchiveCredit";
import { ProblemHead } from "./ProblemHead";
import { EditorialTab } from "./workspace/EditorialTab";
import { StatisticsTab } from "./workspace/StatisticsTab";
import { SubmissionsTab } from "./workspace/SubmissionsTab";
import { ProblemTabs, useProblemTab, type ProblemTab, type ProblemTabItem } from "./workspace/ProblemTabs";
import { ProblemStatementPanel } from "./workspace/ProblemStatementPanel";
import { GradedEditorColumn } from "./workspace/GradedEditorColumn";
import { useCompileFeedback } from "./workspace/use-compile-feedback";
import { runCompileFeedback } from "./workspace/compile-diagnostics";
import { ResultCard } from "./workspace/ResultCard";
import { PracticeHintPanel } from "./workspace/PracticeHintPanel";
import { RUN_SAMPLE_MAX, RunPanel, RunSlotButtons, RunStrip } from "./workspace/RunPanel";
import { useCodeRuns } from "./workspace/use-code-runs";
import { TestStrip } from "./workspace/TestStrip";
import { VerdictPill } from "./workspace/VerdictPill";
import { WorkspaceSplitShell } from "./workspace/WorkspaceSplitShell";
import { pillStateOf, type WorkspaceSubmissionView } from "./workspace/workspace-result";
import { useSolutionDraft, type SolutionDraft } from "./workspace/use-solution-draft";

const RESULT_CARD_ID = "practice-result-card";

/**
 * `EX-HNT01`: the verdicts a hint follows, the server's `PRACTICE_HINT_VERDICTS`.
 * A compile error offers Fix My Error instead.
 */
const HINT_VERDICTS = new Set([
  "wrong_answer",
  "presentation_error",
  "runtime_error",
  "time_limit_exceeded",
  "memory_limit_exceeded",
]);

/**
 * The public problem workspace — `/judge/[slug]`, `EX-PUB01`, docs/27 §10.2.
 *
 * A full-height split under the Judge shell (`EX-PUB11`): statement on the
 * light left pane, the graded editor on the dark right pane, no page scroll —
 * the shell's `h-[calc(100dvh-68px)]` accounts for its own 68px topbar.
 *
 * Statement, bilingual toggle and samples are readable signed out; submitting
 * requires an account, so the submit slot becomes a sign-in call instead of a
 * dead button. The statement, editor and verdict rendering are the shared
 * `workspace/` components, the same ones the contest problem view composes
 * (`EX-PUB08`), so there is one graded editor on the platform and not two.
 */
export function ProblemWorkspaceClient({ slug, locale }: { slug: string; locale: string }) {
  const t = useTranslations("judge.problem");
  const tJudge = useTranslations("judge");
  const tEditor = useTranslations("judge.editor");
  const tHints = useTranslations("judge.hints");
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const viewerKey = authLoading ? "loading" : isAuthenticated ? "member" : "anon";
  // `RV-20`: Sign in to submit comes back to this problem.
  const signInHref = useSignInHref(locale);

  const problemQuery = useJudgeProblem(slug, viewerKey, !authLoading);
  const runtimes = useJudgeRuntimes();
  const problem = problemQuery.data;

  const allowedRuntimes = useMemo(
    () =>
      (runtimes.data?.languages ?? []).flatMap((language) =>
        language.versions.map((version) => ({
          languageKey: language.id,
          languageVersion: version.id,
        })),
      ),
    [runtimes.data],
  );

  // `EX-PUB21`: a new draft opens on the language the reader used last, else
  // C++ at the runner's default version, never the catalogue's first (C99).
  const { draft, pushDraft } = useSolutionDraft(`judge:draft:${slug}`, allowedRuntimes, {
    preferred: preferredJudgeRuntime,
  });
  const draftLanguage = draft?.languageKey;
  const draftVersion = draft?.languageVersion;
  useEffect(() => {
    if (draftLanguage && draftVersion) {
      rememberRuntime({ languageKey: draftLanguage, languageVersion: draftVersion });
    }
  }, [draftLanguage, draftVersion]);
  const [statementLang, setStatementLang] = useState<"en" | "bn">(locale === "bn" ? "bn" : "en");

  // `EX-EDT01`: the sections under the head. Statement and statistics
  // (`EX-STA01`) always; the editorial where the problem has one.
  // `EX-SRC01`: Submissions last, on every problem.
  const availableTabs = useMemo<ProblemTab[]>(
    () =>
      problem?.hasEditorial
        ? ["statement", "editorial", "statistics", "submissions"]
        : ["statement", "statistics", "submissions"],
    [problem?.hasEditorial],
  );
  const { tab, select: selectTab } = useProblemTab(availableTabs);
  const [editorialRevealed, setEditorialRevealed] = useState(false);
  const [programFromStats, setProgramFromStats] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activeSubmissionId, setActiveSubmissionId] = useState<string | null>(null);

  const submitMutation = useSubmitPractice();

  // `EX-EDR02`: the contest page's run panel on practice's own route, custom
  // input included since the route took `stdin`.
  const runMutation = useRunPracticeSample(slug);
  const runs = useCodeRuns({
    run: runMutation.mutateAsync,
    describeError: (err) => {
      const key = judgeErrorMessageKey(err);
      return key ? tJudge(key) : err instanceof ApiError ? err.message : tJudge("runFailed");
    },
    input: draft
      ? {
          languageKey: draft.languageKey,
          languageVersion: draft.languageVersion,
          sourceCode: draft.sourceCode,
        }
      : null,
    firstSampleInput: problem?.samples[0]?.input ?? "",
    openOnRun: problem?.kind === "run_twice" || problem?.kind === "interactive",
  });
  const mySubmissions = useMySubmissions(
    { problemId: problem?.id ?? "", limit: 10 },
    isAuthenticated && !!problem,
  );
  // `EX-EDR01`: the pill describes the submission just sent, else the
  // reader's latest on this problem. Older ones are on the Submissions tab.
  const shownSubmissionId = activeSubmissionId ?? mySubmissions.data?.submissions[0]?.id ?? null;
  const submissionQuery = usePracticeSubmission(shownSubmissionId);

  /**
   * `EX-EDR01`: what floats over the lower editor: the result card. Typing
   * closes it. (The sample runs moved into the run panel, `EX-EDR02`.)
   */
  const [overlay, setOverlay] = useState<"card" | null>(null);
  const [fixOpen, setFixOpen] = useState(false);
  const hintPanelRef = useRef<HTMLElement>(null);
  const [stripOpen, setStripOpen] = useState(true);
  const closeOverlay = useCallback(() => setOverlay(null), []);
  const editDraft = useCallback(
    (next: SolutionDraft) => {
      setOverlay(null);
      pushDraft(next);
    },
    [pushDraft],
  );

  // When a polled verdict completes, the solved badge on this page, the list,
  // and the history are all stale — refresh them once.
  const settledFor = useRef<string | null>(null);
  useEffect(() => {
    const data = submissionQuery.data;
    if (!data || data.status !== "completed") return;
    if (settledFor.current === data.submissionId) return;
    settledFor.current = data.submissionId;
    void queryClient.invalidateQueries({ queryKey: ["judge", "problems"] });
    void queryClient.invalidateQueries({ queryKey: ["judge", "problem"] });
    void queryClient.invalidateQueries({ queryKey: ["judge", "my-submissions"] });
  }, [submissionQuery.data, queryClient]);

  const activeResult = submissionQuery.data;
  const shownView: WorkspaceSubmissionView | null = activeResult
    ? {
        status: activeResult.status,
        verdict: activeResult.result.verdict ?? null,
        testResults: activeResult.result.testResults ?? null,
        hidden: activeResult.result.hidden ?? null,
        timeMs: activeResult.result.timeMs ?? null,
        memoryKb: activeResult.result.memoryKb ?? null,
        languageKey: activeResult.languageKey,
        languageVersion: activeResult.languageVersion,
        createdAt: activeResult.createdAt,
        compileOutput: activeResult.result.compileOutput ?? null,
      }
    : shownSubmissionId
      ? // Just sent and not read back yet: it is in the queue.
        { status: "queued", verdict: null, testResults: null, hidden: null, timeMs: null, memoryKb: null, languageKey: null, languageVersion: null, createdAt: null, compileOutput: null }
      : null;
  const compileFeedback = useCompileFeedback(`judge:draft:${slug}`, draft, shownSubmissionId, shownView);
  const navigationRef = useRef<{ revealLine: (line: number, column?: number) => boolean }>(null);
  const goToLine = useCallback((line: number, column: number) => {
    setOverlay(null);
    navigationRef.current?.revealLine(line, column);
  }, []);
  // `EX-HNT01`: the submission a new hint or a fix is asked from, read by the
  // server; only the reader's own completed one.
  const judgedId = activeResult?.status === "completed" ? activeResult.submissionId : null;
  const wrongSubmissionId =
    judgedId && HINT_VERDICTS.has(activeResult?.result.verdict ?? "") ? judgedId : null;
  const compileErrorSubmissionId =
    judgedId && activeResult?.result.verdict === "compilation_error" ? judgedId : null;
  const openHints = useCallback(() => {
    setOverlay(null);
    selectTab("statement");
    // After the tab has rendered the panel.
    requestAnimationFrame(() => {
      hintPanelRef.current?.scrollIntoView({ behavior: smoothScrollBehavior(), block: "start" });
      hintPanelRef.current?.focus({ preventScroll: true });
    });
  }, [selectTab]);
  const runTrack = runs.diagnosticTab === "custom" ? runs.custom : runs.diagnosticTab === "samples" ? runs.samples : null;
  const runFeedback = runCompileFeedback(runTrack, draft);

  /** `submitted` is the draft "Submit this file" just put in (`EX-EDR03`); else the editor's. */
  const handleSubmit = async (submitted: SolutionDraft | null = draft) => {
    if (!problem || !submitted) return;
    setSubmitError(null);
    compileFeedback.clear();
    runs.showSubmission();
    try {
      const result = await submitMutation.mutateAsync({
        problemId: problem.id,
        languageKey: submitted.languageKey,
        languageVersion: submitted.languageVersion,
        sourceCode: submitted.sourceCode,
      });
      await compileFeedback.remember(result.submissionId, submitted);
      setActiveSubmissionId(result.submissionId);
      setOverlay(null);
      setStripOpen(true);
      runs.showSubmission();
    } catch (err) {
      const key = judgeErrorMessageKey(err);
      setSubmitError(
        key ? tJudge(key) : err instanceof ApiError ? err.message : tJudge("submitFailed"),
      );
    }
  };

  /* ── Loading / missing ─────────────────────────────────────────────────── */

  if (problemQuery.isPending || authLoading) {
    return (
      <div className="flex h-[calc(100dvh-68px)] min-h-0 flex-col gap-4 overflow-y-auto bg-white px-6 py-8 sm:px-10">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-40 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (problemQuery.isError || !problem) {
    const isMissing =
      problemQuery.error instanceof ApiError && problemQuery.error.status === 404;
    return (
      <div className="flex h-[calc(100dvh-68px)] min-h-0 items-center justify-center bg-white px-6">
        <div className="w-full max-w-md rounded-[12px] border border-[#E6E6E6] bg-white p-8 text-center shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
          <p className="text-base font-bold text-[#111111]">
            {isMissing ? t("notFoundTitle") : t("loadFailedTitle")}
          </p>
          <p className="mt-2 text-sm text-[#666666]">
            {isMissing ? t("notFoundBody") : t("loadFailedBody")}
          </p>
          <Link
            href={`/${locale}/judge/problems`}
            className="mt-5 inline-flex h-10 items-center gap-2 rounded-[12px] bg-[#E94D1D] px-4 text-sm font-bold text-white transition-colors duration-200 ease-out hover:bg-[#d64316]"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("backToLibrary")}
          </Link>
        </div>
      </div>
    );
  }

  /* ── Loaded ────────────────────────────────────────────────────────────── */

  const runSamples = problem.samples.slice(0, RUN_SAMPLE_MAX);
  /** `EX-EDR03`: the upload dialog's subtitle and "Your editor has N lines for ...". */
  const problemTitle = locale === "bn" && problem.title_bn ? problem.title_bn : problem.title_en;
  const subtitle = [
    activeResult?.queuePosition && activeResult.status === "queued"
      ? tJudge("queuePosition", { position: activeResult.queuePosition })
      : null,
    // A compilation error ran no test, so it has no score to print: "0 / 18"
    // beside "no test was run" read like a test count (2026-10-02).
    activeResult?.status === "completed" && activeResult.result.verdict !== "compilation_error"
      ? tJudge("scoreValue", { score: activeResult.result.score, maxScore: activeResult.result.maxScore })
      : null,
    activeResult ? new Date(activeResult.createdAt).toLocaleString(locale === "bn" ? "bn-BD" : "en-GB") : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return (
    <div className="h-[calc(100dvh-68px)] min-h-0">
      <WorkspaceSplitShell
        left={
          <div className="h-full overflow-y-auto bg-white">
            {/*
              `page`, not a typed 1440 (`AD-LAY01`, ADR-0071, `docs/03` §5 rule 1).
              The IDE around this owns the viewport; the statement column inside
              it is the one thing that carries a cap, and the cap is the
              ladder's, set once in `--w-page`. It only bites with the editor
              folded away, where this pane is the whole viewport.
            */}
            <Container width="page" className="py-4 sm:py-6">
              {/*
                `EX-PUB21`: the head carries the crumb, both titles, the limits,
                the round and the language toggle; the statement under it
                renders bare in the language chosen there.
              */}
              <ProblemHead
                problem={problem}
                locale={locale}
                lang={statementLang}
                onLangChange={setStatementLang}
                submissionCount={isAuthenticated ? (mySubmissions.data?.total ?? null) : null}
              />

              {availableTabs.length > 1 ? (
                <div className="mt-4">
                  <ProblemTabs
                    idPrefix={`problem-${slug}`}
                    label={t("tabsLabel")}
                    active={tab}
                    onSelect={selectTab}
                    items={availableTabs.map(
                      (key): ProblemTabItem => ({ key, label: t(`tab_${key}`) }),
                    )}
                  />
                </div>
              ) : null}

              <div
                className="mt-5"
                {...(availableTabs.length > 1
                  ? { id: `problem-${slug}-panel`, role: "tabpanel", "aria-labelledby": `problem-${slug}-tab-${tab}` }
                  : {})}
              >
                {tab === "editorial" ? (
                  <EditorialTab
                    slug={slug}
                    lang={statementLang}
                    revealed={editorialRevealed}
                    onReveal={() => setEditorialRevealed(true)}
                  />
                ) : tab === "statistics" ? (
                  <StatisticsTab
                    slug={slug}
                    locale={locale}
                    active
                    onOpenProgram={(submissionId) => {
                      setProgramFromStats(submissionId);
                      selectTab("submissions");
                    }}
                  />
                ) : tab === "submissions" ? (
                  <SubmissionsTab slug={slug} locale={locale} active openProgramId={programFromStats} />
                ) : (
                  <>
                  {isAuthenticated ? (
                    <PracticeHintPanel
                      ref={hintPanelRef}
                      slug={slug}
                      locale={statementLang}
                      wrongSubmissionId={wrongSubmissionId}
                      enabled={(mySubmissions.data?.total ?? 0) > 0}
                    />
                  ) : null}
                  <ProblemStatementPanel
                    problem={problem}
                    locale={locale}
                    bare
                    lang={statementLang}
                    onLangChange={setStatementLang}
                    renderFooter={(lang) => (
                      <ArchiveCredit credit={problem.credit} lang={lang} locale={locale} />
                    )}
                  />
                  </>
                )}
              </div>
            </Container>
          </div>
        }
        right={
          <GradedEditorColumn
            navigationRef={navigationRef}
            markers={runs.diagnosticTab ? runFeedback.markers : compileFeedback.markers}
            draft={draft}
            onDraftChange={editDraft}
            allowedRuntimes={allowedRuntimes}
            runtimesLoading={runtimes.isPending}
            runtimesFailed={runtimes.isError}
            pill={
              <VerdictPill
                state={pillStateOf(shownView)}
                expanded={overlay === "card"}
                onToggle={() => setOverlay((open) => (open === "card" ? null : "card"))}
                controls={RESULT_CARD_ID}
              />
            }
            note={t("practiceNote")}
            runSlot={
              // Signed out already sees one sign-in call in Submit's place - a
              // second, half-working "Run samples" button next to it would be
              // a control that could never do anything.
              isAuthenticated ? (
                <RunSlotButtons
                  runs={runs}
                  disabled={!draft?.sourceCode.trim() || runtimes.isError || allowedRuntimes.length === 0}
                  hasSamples={runSamples.length > 0}
                />
              ) : undefined
            }
            panel={
              isAuthenticated ? (
                <RunPanel
                  runs={runs}
                  samples={runSamples}
                  locale={locale}
                  onGoToLine={goToLine}
                  runTwice={problem.kind === "run_twice"}
                  interactive={problem.kind === "interactive"}
                />
              ) : null
            }
            {...(isAuthenticated ? { onRunShortcut: runs.runShortcut } : {})}
            submitLabel={t("submit")}
            submitting={submitMutation.isPending}
            onSubmit={(submitted) => void handleSubmit(submitted)}
            problemTitle={problemTitle}
            problemLabel={problemTitle}
            action={
              isAuthenticated ? undefined : (
                <Link
                  href={signInHref}
                  className="inline-flex h-10 items-center gap-2 rounded-full bg-primary px-4 text-sm font-bold text-white shadow-sm transition-colors duration-200 ease-out hover:bg-primary/90"
                >
                  <LogIn className="h-4 w-4" />
                  {t("signInToSubmit")}
                </Link>
              )
            }
            error={submitError}
            strip={
              isAuthenticated && runs.stripShowsRun ? (
                <RunStrip runs={runs} />
              ) : shownView && shownSubmissionId ? (
                <TestStrip
                  view={shownView}
                  animationKey={shownSubmissionId}
                  numbered
                  countOnly={false}
                  compileNote={tEditor("compileNotePractice")}
                  open={stripOpen}
                  onToggleOpen={() => setStripOpen((open) => !open)}
                  onDetails={() => setOverlay("card")}
                  detailsControls={RESULT_CARD_ID}
                  locale={locale}
                />
              ) : null
            }
            overlay={
              overlay === "card" && shownView ? (
                <ResultCard
                  id={RESULT_CARD_ID}
                  view={shownView}
                  onGoToLine={goToLine}
                  subtitle={subtitle}
                  countOnly={false}
                  compileNote={tEditor("compileNotePractice")}
                  link={
                    isAuthenticated
                      ? {
                          label: tEditor("cardAllSubmissions"),
                          onClick: () => {
                            setOverlay(null);
                            selectTab("submissions");
                          },
                        }
                      : null
                  }
                  onClose={closeOverlay}
                  locale={locale}
                >
                  {/* `EX-HNT01`: the next move after a wrong verdict or a compile error. */}
                  {isAuthenticated && (wrongSubmissionId || compileErrorSubmissionId) ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {wrongSubmissionId ? (
                        <Button type="button" variant="outlineDark" size="sm" onClick={openHints}>
                          <Lightbulb className="h-3.5 w-3.5" aria-hidden />
                          {tHints("getHint")}
                        </Button>
                      ) : null}
                      {compileErrorSubmissionId ? (
                        <Button type="button" variant="outlineDark" size="sm" onClick={() => setFixOpen(true)}>
                          <Wand2 className="h-3.5 w-3.5" aria-hidden />
                          {tHints("fixMyError")}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </ResultCard>
              ) : null
            }
          />
        }
      />
      {compileErrorSubmissionId && activeResult ? (
        <FixErrorModal
          open={fixOpen}
          onOpenChange={setFixOpen}
          request={{
            languageId: activeResult.languageKey,
            // The server reads the program itself; the answer names it back.
            sourceCode: "",
            outcome: "compilation_error",
            locale: statementLang,
            compileOutput: activeResult.result.compileOutput ?? null,
          }}
          currentSource={draft?.sourceCode ?? ""}
          onApplyFix={(patched) => {
            if (!draft || draft.sourceCode === patched) return false;
            editDraft({ ...draft, sourceCode: patched });
            return true;
          }}
          ask={(input) =>
            fixPracticeError(slug, compileErrorSubmissionId, {
              locale: input.locale,
              idempotencyKey: input.idempotencyKey,
            })
          }
        />
      ) : null}
    </div>
  );
}
